/**
 * OrChoiceModal.tsx — Bug #12: player branch decision for isImmediate OR-choice Event cards.
 *
 * Shown when pendingOrChoice !== null (set by handleNpcCardDraw when a drawn Event card
 * has branchId-annotated effects — Wildfire, Labor_Union_Strikes, Nuclear_Safety_Regulation,
 * Flash_Flood). The player chooses between the "pay" branch (safe, token cost) and the
 * "risk" branch (consequence — decommission / destroy / pollution).
 *
 * Pattern mirrors EnergyDilemmaModal: local latch + boardAnimationActive render gate.
 *
 * VISIBLE WHEN: pendingOrChoice !== null AND boardAnimationActive === false
 * TEST PATH: era >= matching era → roll dice → land on Event tile → draw OR-choice card
 *            → OrChoiceModal renders after HexBoard walk animation completes.
 *            Two buttons (pay / risk). Choosing runs resolveOrChoice(branchId).
 *
 * Reduced-motion: no animations (static decision modal — spec §0.1 rule 9).
 */

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./OrChoiceModal.module.css";

// ---------------------------------------------------------------------------
// OrChoiceModal — outer latch + render gate
// ---------------------------------------------------------------------------

export function OrChoiceModal(): React.ReactElement | null {
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  // R20-GE-6: gate — do not render while card-review beat is active (CDM shows card first).
  const cardReviewBeatActive = useGameStore((s) => s.cardReviewBeatActive);
  const pendingOrChoice = useGameStore((s) => s.pendingOrChoice);
  const resolveOrChoice = useGameStore((s) => s.resolveOrChoice);
  const dismissOrChoice = useGameStore((s) => s.dismissOrChoice);

  // Latch locally so the modal survives pendingOrChoice being cleared before the
  // player sees a flash (same pattern as ScoutModal / EnergyDilemmaModal R13 fix).
  const [activeChoice, setActiveChoice] = useState<{
    cardName: string;
    payLabel: string;
    riskLabel: string;
    payBranchId: string;
    riskBranchId: string;
  } | null>(null);

  useEffect(() => {
    if (pendingOrChoice !== null && activeChoice === null) {
      setActiveChoice({
        cardName: pendingOrChoice.cardName,
        payLabel: pendingOrChoice.payLabel,
        riskLabel: pendingOrChoice.riskLabel,
        payBranchId: pendingOrChoice.payBranchId,
        riskBranchId: pendingOrChoice.riskBranchId,
      });
    }
  }, [pendingOrChoice, activeChoice]);

  if (boardAnimationActive) return null;
  // R20-GE-6: suppress until card-review beat completes (CDM closes first).
  if (cardReviewBeatActive) return null;
  if (activeChoice === null) return null;

  return (
    <OrChoiceModalInner
      key={`or-choice-${activeChoice.cardName}-${activeChoice.payBranchId}`}
      cardName={activeChoice.cardName}
      payLabel={activeChoice.payLabel}
      riskLabel={activeChoice.riskLabel}
      payBranchId={activeChoice.payBranchId}
      riskBranchId={activeChoice.riskBranchId}
      resolveOrChoice={resolveOrChoice}
      dismissOrChoice={dismissOrChoice}
      onComplete={() => setActiveChoice(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner (keyed to reset state on each new offer)
// ---------------------------------------------------------------------------

interface InnerProps {
  cardName: string;
  payLabel: string;
  riskLabel: string;
  payBranchId: string;
  riskBranchId: string;
  resolveOrChoice: (branchId: string) => void;
  dismissOrChoice: () => void;
  onComplete: () => void;
}

function OrChoiceModalInner({
  cardName,
  payLabel,
  riskLabel,
  payBranchId,
  riskBranchId,
  resolveOrChoice,
  dismissOrChoice,
  onComplete,
}: InnerProps): React.ReactElement {
  const handlePay = useCallback(() => {
    resolveOrChoice(payBranchId);
    onComplete();
  }, [resolveOrChoice, onComplete, payBranchId]);

  const handleRisk = useCallback(() => {
    resolveOrChoice(riskBranchId);
    onComplete();
  }, [resolveOrChoice, onComplete, riskBranchId]);

  const handleDismiss = useCallback(() => {
    dismissOrChoice();
    onComplete();
  }, [dismissOrChoice, onComplete]);

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: overlay needs z-index stacking context
      role="dialog"
      aria-modal="true"
      aria-label="Card Decision"
      data-testid="or-choice-modal"
    >
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.icon} aria-hidden="true">
            ⚠
          </span>
          <div className={styles.titleBlock}>
            <div className={styles.title}>DECISION REQUIRED</div>
            <span className={styles.cardBadge} data-testid="or-choice-card-name">
              {cardName}
            </span>
          </div>
        </div>

        {/* Scenario — uses the card text as context */}
        <p className={styles.scenario} data-testid="or-choice-scenario">
          This Event card requires a decision. Choose how your civilization responds.
        </p>

        {/* OR label */}
        <div className={styles.orLabel} aria-hidden="true">
          — CHOOSE ONE —
        </div>

        {/* Choices */}
        <div className={styles.choiceList} data-testid="or-choice-options">
          <button
            type="button"
            className={styles.choiceBtn}
            onClick={handlePay}
            data-testid="or-choice-pay"
          >
            {payLabel}
          </button>

          <button
            type="button"
            className={`${styles.choiceBtn} ${styles.choiceBtnRisk}`}
            onClick={handleRisk}
            data-testid="or-choice-risk"
          >
            {riskLabel}
          </button>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.skipBtn}
            onClick={handleDismiss}
            data-testid="or-choice-skip"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
